import { AttachmentKindEnum } from '@api/READ_ONLY/client_api/Api';
import {
  CameraOutlined,
  CreditCardOutlined,
  ExperimentOutlined,
  FileOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  PictureOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { ChipSelect, type ChipOption } from '@app/components/ui/ChipSelect';
import { Tag } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * What an attached file is — `attachments.kind`, and the two buckets that stand in
 * for it where nobody has said.
 *
 * One module because four surfaces show this: the client card's folder, the visit
 * form's attachment list, the client form's, and the upload dialogs in both. A kind
 * named two ways in two screens is the same defect as a mistranslation.
 */

/**
 * The values the column accepts, from the generated client's `AttachmentKindEnum`.
 *
 * The type is the enum's string union rather than the enum itself: the client and the
 * visits modules each declare that enum and TypeScript enums are nominal, so a file read
 * through one client would not be assignable to the other's. The union is what both widen
 * to; the two PATCH call sites name their own module's member on the way out.
 */
export type AttachmentKind = `${AttachmentKindEnum}`;

/** Display order, which is ours; membership is checked against the generated enum. */
export const ATTACHMENT_KINDS = [
  'REPORT',
  'PHOTO',
  'LAB',
  'PRESCRIPTION',
  'INVOICE',
  'CARE_PLAN',
] as const satisfies readonly AttachmentKind[];

/**
 * A kind, or what the content type can say when there is none.
 *
 * `image` and `document` are NOT values of the column: they are what an
 * unclassified file is shown as, which is all the content type can honestly tell
 * us — it cannot distinguish a lab result from a prescription from an invoice.
 */
export type AttachmentBucket = AttachmentKind | 'image' | 'document';

export const ATTACHMENT_BUCKET_ORDER: AttachmentBucket[] = [...ATTACHMENT_KINDS, 'image', 'document'];

export const ATTACHMENT_BUCKET_LABEL_KEYS: Record<AttachmentBucket, string> = {
  REPORT: 'attachment-kind-report',
  PHOTO: 'attachment-kind-photo',
  LAB: 'attachment-kind-lab',
  PRESCRIPTION: 'attachment-kind-prescription',
  INVOICE: 'attachment-kind-invoice',
  CARE_PLAN: 'attachment-kind-care-plan',
  image: 'attachment-kind-image',
  document: 'attachment-kind-document',
};

export const ATTACHMENT_BUCKET_STYLE: Record<AttachmentBucket, { icon: React.ReactNode; color: string; bg: string }> = {
  REPORT: { icon: <FileTextOutlined />, color: '#0284c7', bg: '#f0f9ff' },
  PHOTO: { icon: <CameraOutlined />, color: '#7c3aed', bg: '#f5f3ff' },
  LAB: { icon: <ExperimentOutlined />, color: '#0d9488', bg: '#f0fdfa' },
  PRESCRIPTION: { icon: <MedicineBoxOutlined />, color: '#c2410c', bg: '#fff7ed' },
  INVOICE: { icon: <CreditCardOutlined />, color: '#475569', bg: '#f8fafc' },
  CARE_PLAN: { icon: <ProfileOutlined />, color: '#b45309', bg: '#fffbeb' },
  image: { icon: <PictureOutlined />, color: '#7c3aed', bg: '#f5f3ff' },
  document: { icon: <FileOutlined />, color: '#475569', bg: '#f8fafc' },
};

const KNOWN_KINDS: ReadonlySet<string> = new Set(ATTACHMENT_KINDS);

export const isAttachmentKind = (value: string | null | undefined): value is AttachmentKind =>
  !!value && KNOWN_KINDS.has(value);

/**
 * The bucket a file is shown under: its kind when it has a recognised one, else
 * what its content type can say.
 *
 * A kind this build has no label for falls back the same way rather than putting a
 * raw enum name on the screen — a value added to the column before the UI knows it
 * must read as "unclassified", not as `SOMETHING_NEW`.
 */
export const attachmentBucketOf = (attachment: {
  kind?: string | null;
  contentType?: string | null;
}): AttachmentBucket => {
  if (isAttachmentKind(attachment.kind)) return attachment.kind;
  return (attachment.contentType ?? '').startsWith('image/') ? 'image' : 'document';
};

/* ================= Components ================= */

/**
 * A file's kind as a tag, or nothing at all when it has none.
 *
 * Nothing rather than an "unclassified" tag: until something starts setting the
 * column every file is unclassified, and a tag on all of them says nothing while
 * taking a row's worth of attention.
 */
export const AttachmentKindTag: React.FC<{ kind?: string | null }> = ({ kind }) => {
  const { t } = useTranslation();

  if (!isAttachmentKind(kind)) return null;

  const style = ATTACHMENT_BUCKET_STYLE[kind];

  return (
    <Tag className="!mr-0" style={{ color: style.color, background: style.bg, borderColor: `${style.color}44` }}>
      {t(ATTACHMENT_BUCKET_LABEL_KEYS[kind])}
    </Tag>
  );
};

type AttachmentKindEditableProps = {
  value?: string | null;
  onChange: (kind: AttachmentKind | null) => void;
  /** True while the change is in flight. The chip holds its place and stops taking clicks. */
  saving?: boolean;
};

/**
 * A file's kind, changed in place.
 *
 * Resting state is a chip, not a dropdown: a list of files is read far more often
 * than it is re-tagged, and a select on every row turns the list into a form. The
 * menu opens under the chip rather than replacing it, so the name and the actions
 * sharing the row do not shift while you pick.
 */
export const AttachmentKindEditable: React.FC<AttachmentKindEditableProps> = ({ value, onChange, saving }) => {
  const { t } = useTranslation();
  const kind = isAttachmentKind(value) ? value : null;

  const options: ChipOption<AttachmentKind>[] = ATTACHMENT_KINDS.map((k) => ({
    value: k,
    label: t(ATTACHMENT_BUCKET_LABEL_KEYS[k]),
    color: ATTACHMENT_BUCKET_STYLE[k].color,
    bg: ATTACHMENT_BUCKET_STYLE[k].bg,
    icon: <span className="flex text-[11px] leading-none">{ATTACHMENT_BUCKET_STYLE[k].icon}</span>,
  }));

  return (
    <ChipSelect<AttachmentKind>
      value={kind}
      options={options}
      onChange={onChange}
      addLabel={t('attachment-kind-add')}
      clearLabel={t('attachment-kind-placeholder')}
      title={t(kind ? 'attachment-kind-change' : 'attachment-kind-add')}
      saving={saving}
    />
  );
};
